import { ApiCall } from '~/adapters/services/shared/apiCall';
import { useMemo } from 'react';
import { useUploadApiCall } from '~/adapters/services/upload/uploadApiCall';
import { PresignedUrlResponse, AnalyzeFileResponse, PresignedUrlFields } from '~/adapters/services/upload/types';

class UploadRepository {
    constructor(private readonly uploadsApiCall: ApiCall) {}

    public getPresignedUrl = async (data: { fileName: string; contentType: string }): Promise<PresignedUrlResponse> => {
        const result = await this.uploadsApiCall('/uploads/presigned-url', {
            attachImpersonate: false,
            method: 'POST',
            headers: {
                accept: 'application/json',
                'Content-Type': 'application/json',
            },
            data,
        });
        return result.data as PresignedUrlResponse;
    };

    public analyzeFiles = async (data: { keys: string[] }): Promise<AnalyzeFileResponse[]> => {
        const response = await Promise.all(
            data.keys.map(async (key) => {
                const result = await this.uploadsApiCall(
                    `/uploads/presigned-url/analysis?key=${key}&gamutMapping=true`,
                    {
                        attachImpersonate: false,
                        method: 'GET',
                        headers: {
                            accept: 'application/json',
                        },
                    },
                );
                return result.data as AnalyzeFileResponse;
            }),
        );
        return response;
    };

    public uploadFilesToS3WithPresignedUrl = async (params: {
        files: {
            presignedUrl: string;
            presignedFields: PresignedUrlFields;
            file: File;
            name: string;
            contentType: string;
        }[];
    }) => {
        const uploadPromises = params.files.map(async (file) => {
            const formData = new FormData();

            const fieldsToAppend = [...Object.entries(file.presignedFields), ['Content-Type', file.contentType]];
            fieldsToAppend.forEach(([field, value]) => {
                formData.append(field, value);
            });
            formData.append('file', file.file);

            const uploadResponse = await window.fetch(file.presignedUrl, {
                method: 'POST',
                body: formData,
            });
            if (!uploadResponse.ok) {
                if (uploadResponse.status === 413) {
                    throw new Error('File size too large');
                }
                throw new Error('File unsupported');
            }
        });

        await Promise.all(uploadPromises);
    };
}

export const useUploadRepository = () => {
    const uploadsApiCall = useUploadApiCall();

    return useMemo(() => new UploadRepository(uploadsApiCall), []);
};
